import { fillYearsFromProfiles } from "@/content/staff-facts";
import { createFileRoute, Link } from "@tanstack/react-router";
import heroCoast from "@/assets/hero-play.webp";

import { heroCoastImage, heroCoastAvifSrc } from "@/lib/responsive-images";
import { getHiddenGate } from "@/lib/settings.functions";
import { staffPortraitsQuery, type StaffPortraitMap } from "@/lib/staff-portraits";
import { portraitBackground } from "@/lib/portrait-backgrounds";
import { staffProfilesQuery, type StaffProfileMap } from "@/lib/staff-profiles";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { pathFor } from "@/lib/i18n";
import { CtaBand } from "@/components/site/Page";
import { GuideLinks } from "@/components/site/Guides";
import { KnowledgeHubTeaser } from "@/components/site/KnowledgeHubTeaser";
import { HomeHero } from "@/components/site/HomeHero";
import { HiddenGateSlot } from "@/components/site/HiddenGateSlot";
import { HomeTeachers, HomeTrust, HomeWhyUs } from "@/components/site/HomeSections";
import { HomeHistory } from "@/components/site/HomeHistory";
import { HomeDeferredContent } from "@/components/site/HomeDeferredContent";
import { SchoolValues } from "@/components/site/SchoolValues";
import {
  ComparisonTable,
  EnrollButtons,
  OnboardingNote,
} from "@/components/site/Highlights";
import { ProofBand } from "@/components/site/ProofBand";
import { CampHomeFeature } from "@/components/site/CampHomeFeature";
import { MffBlock, TrustStrip } from "@/components/site/MffSection";
import { Testimonials } from "@/components/site/Testimonials";
import { listPublishedTestimonials } from "@/lib/testimonials.functions";
import { PrincipalWord } from "@/components/site/PrincipalWord";
import { safeLoader } from "@/lib/safe-loader";
import classroom from "@/assets/home-primary.webp";
import campus from "@/assets/campus-sign.webp";
import preschoolPlay from "@/assets/home-preschool.webp";
import preschoolClass from "@/assets/home-preschool-class.webp";
import schoolyard from "@/assets/schoolyard-basket.webp";
import football from "@/assets/profile-football-mff.webp";
import padel from "@/assets/profile-padel.webp";

export const Route = createFileRoute("/es/")({
  loader: async ({ context }) => {
    const [gate, data, portraitData, profileData, testimonials] = await Promise.all([
      safeLoader("es.home.hidden-gate", () => context.queryClient.ensureQueryData({ queryKey: ["hidden-gate"], queryFn: () => getHiddenGate() }), { enabled: false }),
      safeLoader("es.home.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined),
      // Porträttdata måste finnas redan vid serverrendering, annars laddas lärarbilderna sent.
      safeLoader("es.home.portraits", () => context.queryClient.ensureQueryData(staffPortraitsQuery), { portraits: {} }),
      safeLoader("es.home.profiles", () => context.queryClient.ensureQueryData(staffProfilesQuery), { profiles: {} }),
      // Omdömena hämtas redan på servern så sidan inte hoppar när de dyker upp.
      safeLoader("es.home.testimonials", () => context.queryClient.ensureQueryData({
        queryKey: ["testimonials", "published", "home", "es"],
        queryFn: () => listPublishedTestimonials({ data: { limit: 12, locale: "es", homeOnly: true } }),
      }), []),
    ]);
    const backgroundId = data?.content?.["settings"]?.["portrait_background"]?.es as string | undefined;
    return { hiddenGate: gate.enabled, seo: resolveSeo(data?.content, "start", "es"), profiles: profileData.profiles as StaffProfileMap, portraits: portraitData.portraits as StaffPortraitMap, backgroundId, testimonials };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl("/es") },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImage("/og/start-v1.jpg"),
        { property: "og:locale", content: "es_ES" },
      ],
      links: [
        ...seoLinks("/es"),
        {
          rel: "preload",
          as: "image",
          type: "image/avif",
          href: heroCoastAvifSrc,
          imageSrcSet: heroCoastImage.avif,
          imageSizes: "100vw",
          fetchPriority: "high",
        } as never,
      ],
    };
  },
  component: SpanishHome,
});

const comparison = [
  {
    row: "Plan de estudios",
    us: "Currículo y programas suecos",
    other: "Currículo español o internacional",
  },
  {
    row: "Idioma de enseñanza",
    us: "Sueco, con español e inglés cada día",
    other: "Español o inglés",
  },
  {
    row: "Profesorado",
    us: "Titulación docente sueca en toda la enseñanza en sueco",
    other: "Varía según el centro",
  },
  {
    row: "Regreso a Suecia",
    us: "El alumno continúa con el mismo currículo",
    other: "A menudo requiere adaptación",
  },
  {
    row: "Tamaño de los grupos",
    us: "Como máximo 16 alumnos por clase (curso 2026/27)",
    other: "Normalmente grupos más grandes",
  },
  {
    row: "Secundaria y bachillerato",
    us: "Estudios suecos a distancia con apoyo presencial",
    other: "Sistema local o IB",
  },
];

const program = [
  {
    to: "/forskola" as const,
    image: preschoolPlay,
    alt: "Niños construyendo juntos en la mesa del preescolar",
    label: "3–5 años",
    title: "Preescolar",
    text: "Juego y aprendizaje con currículo sueco y contacto natural con el español y el inglés.",
  },
  {
    to: "/forskoleklass" as const,
    image: preschoolClass,
    alt: "Niños de la clase preescolar sentados en una mesa baja practicando números con lápices",
    label: "5–6 años",
    title: "Clase preescolar",
    text: "Una transición suave hacia la escuela con juego, lenguaje e interacción social.",
  },
  {
    to: "/grundskola" as const,
    image: classroom,
    alt: "Alumnos trabajando en el aula del colegio",
    label: "Curso F–6",
    title: "Primaria",
    text: "Currículo sueco con el objetivo de contar con docentes titulados en Suecia para la enseñanza en sueco y grupos reducidos.",
  },
  {
    to: "/fotbollsakademi" as const,
    image: football,
    alt: "Entrenamiento de fútbol en el campo de hierba de Estepona",
    label: "Perfil deportivo",
    title: "Academia de fútbol del Malmö FF",
    text: "Entrenamiento con la metodología del Malmö FF combinado con los estudios en un entorno internacional.",
  },
  {
    to: "/padel-tennis" as const,
    image: padel,
    alt: "Pista de pádel en la Costa del Sol",
    label: "Perfil deportivo",
    title: "Pádel y tenis",
    text: "Entrenamiento de alto nivel en la Costa del Sol, en grupos reducidos y con entrenador propio.",
  },
];

const steps = [
  { n: "1", title: "Envía la solicitud", text: "Completa el formulario y verás la tarifa al instante." },
  { n: "2", title: "Conoce el colegio", text: "Visítanos en persona o reúnete con nosotros en línea." },
  { n: "3", title: "Confirmación de plaza", text: "Te responderemos con la decisión y la fecha de inicio." },
];

function SpanishHome() {
  const { text, list } = useContent("start");
  const { hiddenGate, profiles, portraits, backgroundId, testimonials } = Route.useLoaderData();
  const values = list("values");

  return (
    <>
      <HiddenGateSlot enabled={hiddenGate} />
      <HomeHero
        locale="es"
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
        primaryLabel={text("hero.primary_button")}
        secondaryLabel={text("hero.secondary_button")}
        note={text("hero.note")}
        imageAlt="Patio escolar soleado con dibujos de tiza, comba, pelota roja y juguetes de madera bajo un olivo"
      />

      <ProofBand locale="es" photos={list("proof.photos")} />

      <CampHomeFeature locale="es" />

      <TrustStrip locale="es" />

      <section id="varden" className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
        <h2 className="text-2xl font-semibold sm:text-4xl">{text("values.title")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {text("values.intro")}
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((v) => (
            <div key={v.title} className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h3 className="text-base font-semibold">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{fillYearsFromProfiles(v.text, profiles)}</p>
            </div>
          ))}
        </div>
      </section>

      <SchoolValues compact />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
        <h2 className="text-2xl font-semibold sm:text-4xl">El colegio y nuestros perfiles</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          Una jornada escolar que combina la educación sueca con idiomas, cultura y deporte en el
          sur de España.
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {program.map((item) => (
            <Link
              key={`${item.to}:${item.title}`}
              to={pathFor(item.to, "es") as never}
              className="group overflow-hidden rounded-xl border border-border bg-card shadow-soft transition-colors hover:border-primary/40"
            >
              <img
                src={item.image}
                alt={item.alt}
                loading="lazy"
                className="h-56 w-full object-cover"
              />
              <div className="p-7">
                <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">
                  {item.label}
                </p>
                <h3 className="mt-3 text-xl font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                <span className="mt-4 inline-block text-sm text-primary group-hover:underline">
                  Leer más
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <MffBlock locale="es" />

      <HomeTeachers locale="es" profiles={profiles} portraits={portraits} background={portraitBackground(backgroundId)} />

      <Testimonials title={text("testimonials.title")} items={list("testimonials")} locale="es" initialReviews={testimonials} />

      <HomeTrust locale="es" />

      <PrincipalWord locale="es" quote={text("principal.quote")} profiles={profiles} portraits={portraits} background={portraitBackground(backgroundId)} />

      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <h2 className="text-2xl font-semibold sm:text-3xl">
            ¿Colegio sueco o una alternativa local?
          </h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
            Ambas opciones pueden funcionar. La elección depende del tiempo que la familia vaya a
            vivir en España y de los planes posteriores.
          </p>
          <ComparisonTable rows={comparison} locale="es" />
          <p className="mt-4 text-xs text-muted-foreground">
            La comparación es general. La oferta varía entre los centros de la zona; conviene
            visitar varios antes de decidir.
          </p>
          <p className="mt-4">
            <Link
              to="/es/colegio-sueco-internacional-o-espanol"
              className="font-medium text-primary link-quiet"
            >
              Leer la comparativa completa: colegio sueco, internacional o español
            </Link>
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-14 px-5 pt-12 pb-14 sm:space-y-20 sm:pt-16 sm:pb-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold sm:text-3xl">Enseñanza con raíces suecas</h2>
            <ul className="mt-6 space-y-3 text-base leading-relaxed text-foreground/90">
              <li>Currículo, programas y horario suecos: aspiramos a contar con docentes titulados en Suecia para toda la enseñanza en sueco.</li>
              <li>Español e inglés ampliados desde infantil hasta 6.º curso.</li>
              <li>Informes escritos en todos los cursos hasta 6.º incluido.</li>
              <li>Herramientas digitales: los iPads y los ordenadores forman parte del aprendizaje.</li>
              <li>Clases reducidas y actividades con edades mixtas.</li>
            </ul>
          </div>
          <img
            src={campus}
            alt="Cartel de Swedish International School en la entrada del colegio"
            loading="lazy"
            className="rounded-xl object-cover shadow-soft"
            width={1600}
            height={1067}
          />
        </div>

        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img
            src={schoolyard}
            alt="Patio del colegio con canasta de baloncesto y techo de sombra"
            loading="lazy"
            className="rounded-xl object-cover shadow-soft lg:order-2"
            width={1400}
            height={933}
          />
          <div>
            <h2 className="text-2xl font-semibold sm:text-3xl">Salud, seguridad y comunidad</h2>
            <ul className="mt-6 space-y-3 text-base leading-relaxed text-foreground/90">
              <li>Comida casera y meriendas saludables todos los días.</li>
              <li>Patio para el juego y el movimiento, con baloncesto y tenis de mesa.</li>
              <li>Un entorno seguro donde cada alumno es visto, física y emocionalmente.</li>
              <li>Contacto estrecho con el profesorado para seguir de cerca los estudios.</li>
              <li>Actividades extraescolares hasta las 16.30.</li>
            </ul>
            <Link
              to={pathFor("/gemenskap", "es") as never}
              className="mt-6 inline-block text-sm text-primary link-quiet"
            >
              Así acogemos a las familias
            </Link>
          </div>
        </div>

      </section>

      <HomeWhyUs locale="es" />

      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <h2 className="text-2xl font-semibold">Tres pasos para empezar</h2>
          <ol className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-5">
            {steps.map((s) => (
              <li key={s.n} className="rounded-xl bg-card p-6 shadow-soft">
                <span className="font-display text-2xl font-semibold text-primary">{s.n}</span>
                <h3 className="mt-2 text-lg font-semibold">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
          <EnrollButtons className="mt-8" locale="es" />
          <p className="mt-3 text-sm text-muted-foreground">
            Aceptamos solicitudes durante todo el curso, también a mitad de trimestre.
          </p>
          <OnboardingNote className="mt-8 bg-card" locale="es" />
        </div>
      </section>

      <HomeHistory locale="es" />

      <HomeDeferredContent locale="es" />

      <div className="pt-14 sm:pt-16">
        <KnowledgeHubTeaser locale="es" />
      </div>

      <div className="pt-14 sm:pt-16">
        <GuideLinks title="Guías para familias que se mudan aquí" locale="es" />
        <p className="mx-auto -mt-6 max-w-3xl px-5 pb-10 text-sm text-muted-foreground sm:pb-12">
          ¿Quieres más detalles? El centro de recursos explica cada tema paso a paso.
        </p>
      </div>

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
